import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { Briefcase } from "lucide-react";
import { useRbac } from "../../context";

// Define a type for SVG icons to ensure type safety
type IconComponent = React.ReactElement<React.SVGProps<SVGSVGElement>>;

interface MenuItem {
  label: string;
  icon: IconComponent;
  link: string;
  id: string;
  disabled?: boolean;
}

export interface SidebarProps {
  menuItems: Array<{
    icon: React.ReactNode;
    label: string;
    link: string;
    id: string;
    isDisabled?: boolean;
    onClick: (e: React.MouseEvent) => void;
    className?: string;
    permission?: string;
  }>;
  isSidebarOpen: boolean;
  closeSidebar: () => void;
  isMobile: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  isDisabled?: boolean;
  
}

const Sidebar: React.FC<SidebarProps> = ({
  menuItems,
  isSidebarOpen,
  closeSidebar,
  isMobile,
  isCollapsed,
  setIsCollapsed
}) => {
  const [activeItem, setActiveItem] = useState<string>("Dashboard");
  const { user } = useRbac();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const menuScrollRef = useRef<HTMLDivElement>(null);

  // Set active item based on the current route
  useEffect(() => {
    const matchedItem = menuItems.find((item) => item.link === currentPath);
    if (matchedItem) {
      setActiveItem(matchedItem.label);
    }
  }, [currentPath, menuItems]);

  // Scroll to active item when it changes or on mount
  useEffect(() => {
    if (menuScrollRef.current && activeItem) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const activeElement = menuScrollRef.current?.querySelector(
          `[data-active-item="${activeItem}"]`
        ) as HTMLElement;
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 150);
    }
  }, [activeItem, menuItems]);

  // Reset isCollapsed when switching between mobile and desktop modes
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false); // Ensure sidebar is not collapsed on mobile
    }
  }, [isMobile, setIsCollapsed]);

  const handleChangeContent = useCallback(
    (link: string, label: string, disabled?: boolean) => {
      if (disabled) {
        return; // Don't navigate if item is disabled
      }
      setActiveItem(label);
      navigate(link);
      if (isMobile) closeSidebar();
    },
    [navigate, isMobile, closeSidebar]
  );

 

  const isExpanded = !isCollapsed || (isMobile && isSidebarOpen);

  return (
    <>
      {/* Overlay for mobile (visible when sidebar is open) */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ease-in-out"
          onClick={closeSidebar}
          aria-label="Close sidebar overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={[
          "bg-white border-gray-200 flex flex-col transition-all duration-300 ease-in-out top-0 left-0 h-full",
          isMobile
            ? isSidebarOpen
              ? "translate-x-0 fixed z-50 w-3/4 sm:w-64"
              : "-translate-x-full fixed z-50 w-3/4 sm:w-64"
            : "translate-x-0 static m-3 mr-0",
          isCollapsed && !isSidebarOpen ? "w-16" : "w-64",
        ].join(" ")}
        style={
          {
            borderRadius: "16px",
            border: "1px solid #d3d3d3",
            height: isMobile ? "100vh" : "calc(100vh - 1.5rem)",
          } as React.CSSProperties
        }
        role="navigation"
        aria-label="Sidebar navigation"
      >
        {/* Header with Logo and Close Button */}
        <div className="border-b border-gray-100 flex items-center justify-between relative py-2 px-3">
          <div className="flex items-center flex-1 justify-center">
            <div className="overflow-hidden">
              <img
                src="/assets/images/earlyjobs.png"
                alt="Early Jobs Logo"
                className={[
                  "overflow-hidden transition-all duration-200", 
                  isExpanded
                    ? isMobile
                      ? "w-20 h-20"
                      : "w-24 h-24"
                    : "h-12",
                ].join(" ")}
              />
            </div>
          </div>

          {/* Close Button (visible on mobile) */}
          {isMobile && (
            <button
              onClick={closeSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              aria-label="Close sidebar"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-600"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <div ref={menuScrollRef} className="flex-1 pt-4 overflow-y-scroll overflow-x-hidden">
          <ul className="space-y-1 px-3">
{menuItems.map((item) => {
  // Skip AI Mode - it's now in the top header
  if (item.label === "Ai Mode") {
    return null;
  }
  
  // Default case for all other menu items
  return (
    <li 
      key={item.id}
      data-active-item={item.label}
    >
      <button
        onClick={() => handleChangeContent(item.link, item.label, item.isDisabled)}
        disabled={item.isDisabled || item.permission==="tpoFeatures"}
        style={isCollapsed ? { padding:"7px 16px" } : {}}
        className={`w-full flex items-center px-3 py-2.5 text-sm font-medium transition-colors duration-200 group relative ${
          isCollapsed ? "justify-center rounded-[100%]" : "rounded-lg"
        } ${
          item.isDisabled
            ? "text-gray-400 bg-gray-50 cursor-not-allowed opacity-60"
            : activeItem === item.label
            ? `bg-[#FFF5F2] text-[#E66A4E] border border-[#E66A4E]`
            : "text-gray-700 hover:bg-[#EC6E51]/90 hover:text-white cursor-pointer"
        }`}
        aria-current={activeItem === item.label ? "page" : undefined}
        title={item.isDisabled ? "Complete onboarding to access this feature" : ""}
      >
        <span
          className={`flex-shrink-0 ${
            item.isDisabled
              ? "text-gray-300"
              : activeItem === item.label
              ? "text-[#EE7D63]"
              : "text-gray-400 group-hover:text-white"
          }`}
        >
          {item.icon}
        </span>

        {(!isCollapsed || (isMobile && isSidebarOpen)) && (
          <span className="ml-3 truncate">{item.label==="My Hiring Managers" && (user.role==='HM' || user.role==='AGENCY' || user.role==='AGENCY_ADMIN')?"My Recruiters":item.label}</span>
        )}

        {/* Lock icon for disabled items */}
        {((item.isDisabled || item.permission==="tpoFeatures") && (!isCollapsed || (isMobile && isSidebarOpen))) && (
          <span className="ml-auto">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-gray-300"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <circle cx="12" cy="7" r="4"/>
              <path d="M12 1v6"/>
              <path d="M17 11V7a5 5 0 0 0-10 0v4"/>
            </svg>
          </span>
        )}

        {/* Tooltip for collapsed state (desktop only, disabled on mobile) */}
        {isCollapsed && !isMobile && !isSidebarOpen && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-[#EE7D63] text-white text-xs rounded opacity-0 invisible group-hover:opacity-80 group-hover:visible transition-opacity duration-200 whitespace-nowrap">
            {item.isDisabled ? "Complete onboarding first" : item.label}
            <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-[#EE7D63] rotate-45"></div>
          </div>
        )}
      </button>
    </li>
  );
})}
          </ul>
        </div>
      </div>

      <style>{`
        .transition-all {
          transition: all 0.3s ease-in-out;
        }

        /* Ensure buttons have a minimum tap target size for mobile */
        button {
          
        }

        /* Focus styles for accessibility on mobile */
        button:focus {
          outline: 2px solid #EE7D63;
          outline-offset: 2px;
        }

        /* Smooth scrolling for the sidebar menu on mobile */
        .overflow-y-auto {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </>
  );
};

export default Sidebar;